import { Show, createEffect, onCleanup, type Component } from 'solid-js';

import type { GameStore } from '../state/store';

/**
 * The level-up banner: a struck rune and the new level, over the middle of the screen.
 *
 * ── Why it exists ───────────────────────────────────────────────────────────
 *
 * Levelling had **no visual at all**. It played a sound, refreshed the character frame,
 * and that was the whole of it — the single most important thing that happens to a
 * character was a number changing on a panel most players do not have open. Asked for
 * directly: *levelling up should have a special animation overlay and display should be
 * printed.*
 *
 * ── Why it does not block ───────────────────────────────────────────────────
 *
 * `pointer-events: none`, and no modal. A level-up lands **mid-fight** far more often
 * than not — that is when experience arrives — so anything that took the pointer, paused
 * the world or wanted dismissing would be a panel over a monster that is still swinging.
 * It plays over the top and gets out of the way.
 *
 * ── Why the timer is here and the duration is not ───────────────────────────
 *
 * The store holds the level and nothing else. This component clears it when the animation
 * ends, and it learns *when* that is from the animation itself rather than from a number
 * copied out of the stylesheet — a duration written in both places is one that drifts the
 * first time the animation is retimed, and the symptom is a banner that vanishes early or
 * sticks.
 *
 * `animationend` is the signal, with a timer only as the backstop for the environment
 * where no animation runs at all: **jsdom fires no animation events**, and a banner that
 * never cleared would leak into the next assertion in the same suite.
 */

/** The backstop, in ms. Only ever reached where CSS animations do not run. */
const FALLBACK_MS = 2600;

export const LevelUpOverlay: Component<{ store: GameStore }> = (props) => {
  const level = () => props.store.levelUp();

  createEffect(() => {
    if (level() === null) return;
    const timer = setTimeout(() => props.store.clearLevelUp(), FALLBACK_MS);
    onCleanup(() => clearTimeout(timer));
  });

  return (
    <Show when={level() !== null}>
      <div
        class="levelup"
        data-testid="levelup-overlay"
        // A live region rather than a dialog: it announces and does not ask. `polite`
        // because it must not cut across a screen reader mid-sentence during a fight.
        role="status"
        aria-live="polite"
        onAnimationEnd={() => props.store.clearLevelUp()}
      >
        <div class="levelup__burst" aria-hidden="true" />
        <p class="levelup__word">Level Up</p>
        <p class="levelup__level" data-testid="levelup-level">
          {level()}
        </p>
        <p class="levelup__note">Health and mana restored</p>
      </div>
    </Show>
  );
};
